import { createAddRowColumnFunctions } from "../tableManipulation/addRowColumn";
import { createGridLineMovementFunctions } from "../utils/gridLineMovement";
import { useMemo, useCallback } from "react";

/**
 * Hook for grid line operations - coordinates grid line utilities with state management
 * Provides interface for grid line movement, placement, and table manipulation
 */
const useGridLineOperations = (
  pdfState,
  gridLineState,
  uiState,
  drawingState,
  refs,
  tableState,
  recalculateCellsFromGridLinesWrapper
) => {
  const { currentPage } = pdfState;
  const { tableData, selectedTable } = tableState;
  const {
    dynamicGridLines,
    setDynamicGridLines,
    isDraggingGridLine,
    setIsDraggingGridLine,
    draggingGridLine,
    setDraggingGridLine,
    gridLineStartPos,
    setGridLineStartPos,
    originalGridLines,
    setOriginalGridLines,
    gridLineDragMode,
    setGridLineDragMode,
    isPlacingGridLine,
    setIsPlacingGridLine,
    placingGridLineType,
    setPlacingGridLineType,
    setPlacingGridLinePosition,
  } = gridLineState;

  const { isResizing } = uiState;
  const { drawingMode, setDrawingMode, setIsDrawing } = drawingState;
  const { canvasRef, highlightContainerRef, pdfPageDimensions } = refs;

  // Create grid line movement functions using extracted utility
  const gridLineMovementFunctions = useMemo(
    () =>
      createGridLineMovementFunctions(
        canvasRef,
        highlightContainerRef,
        dynamicGridLines,
        setDynamicGridLines,
        currentPage,
        pdfPageDimensions,
        drawingMode,
        isResizing,
        gridLineDragMode,
        isDraggingGridLine,
        setIsDraggingGridLine,
        draggingGridLine,
        setDraggingGridLine,
        gridLineStartPos,
        setGridLineStartPos,
        originalGridLines,
        setOriginalGridLines
      ),
    [
      dynamicGridLines,
      currentPage,
      drawingMode,
      isResizing,
      gridLineDragMode,
      isDraggingGridLine,
      draggingGridLine,
      gridLineStartPos,
      originalGridLines,
      canvasRef,
      highlightContainerRef,
      pdfPageDimensions,
      setDynamicGridLines,
      setIsDraggingGridLine,
      setDraggingGridLine,
      setGridLineStartPos,
      setOriginalGridLines,
    ]
  );

  // Create add row/column functions using extracted utility
  const addRowColumnFunctions = useMemo(
    () =>
      createAddRowColumnFunctions(
        tableData,
        dynamicGridLines,
        setDynamicGridLines,
        currentPage,
        isPlacingGridLine,
        setIsPlacingGridLine,
        placingGridLineType,
        setPlacingGridLineType,
        setPlacingGridLinePosition,
        setDrawingMode,
        setIsDrawing,
        setGridLineDragMode,
        originalGridLines,
        setOriginalGridLines,
        canvasRef,
        highlightContainerRef,
        pdfPageDimensions
      ),
    [
      tableData,
      dynamicGridLines,
      currentPage,
      isPlacingGridLine,
      placingGridLineType,
      originalGridLines,
      canvasRef,
      highlightContainerRef,
      pdfPageDimensions,
      setDynamicGridLines,
      setIsPlacingGridLine,
      setPlacingGridLineType,
      setPlacingGridLinePosition,
      setDrawingMode,
      setIsDrawing,
      setGridLineDragMode,
      setOriginalGridLines,
    ]
  );

  const {
    onMouseDown,
    onMouseMove,
    onMouseUp,
    startGridLineDrag: startGridLineDragRaw,
    continueGridLineDrag,
    finishGridLineDrag,
    addGridLineDragEventListeners,
  } = gridLineMovementFunctions || {};

  // Create a wrapper that properly starts drag AND sets up mouse listeners
  const startGridLineDrag = useCallback(
    (event, lineType, lineIndex) => {
      // Start the drag state
      if (startGridLineDragRaw) {
        startGridLineDragRaw(event, lineType, lineIndex);

        // Add mouse listeners for the drag operation
        if (addGridLineDragEventListeners) {
          addGridLineDragEventListeners(
            tableData,
            selectedTable,
            gridLineState.setPendingGridLineChanges,
            gridLineState.setShowGridLineConfirmation,
            recalculateCellsFromGridLinesWrapper,
            null // placingGridLineType
          );
        }
      }
    },
    [
      startGridLineDragRaw,
      addGridLineDragEventListeners,
      tableData,
      selectedTable,
      gridLineState.setPendingGridLineChanges,
      gridLineState.setShowGridLineConfirmation,
      recalculateCellsFromGridLinesWrapper,
    ]
  );

  const {
    addRowAbove,
    addRowBelow,
    addColumnLeft,
    addColumnRight,
    startPlacingGridLine,
    confirmGridLinePlacement,
    cancelGridLinePlacement,
    performColumnSplit,
  } = addRowColumnFunctions || {};

  // Provide convenience methods
  const getGridLineStats = useCallback(
    () => ({
      totalLines: dynamicGridLines?.length || 0,
      isDragging: isDraggingGridLine,
      isPlacing: isPlacingGridLine,
      dragMode: gridLineDragMode,
      placingType: placingGridLineType,
    }),
    [
      dynamicGridLines?.length,
      isDraggingGridLine,
      isPlacingGridLine,
      gridLineDragMode,
      placingGridLineType,
    ]
  );

  const isInGridLineMode = useCallback(() => {
    return isDraggingGridLine || isPlacingGridLine || gridLineDragMode !== null;
  }, [isDraggingGridLine, isPlacingGridLine, gridLineDragMode]);

  const resetGridLineState = useCallback(() => {
    setIsDraggingGridLine(false);
    setDraggingGridLine(null);
    setGridLineStartPos(null);
    setIsPlacingGridLine(false);
    setPlacingGridLineType(null);
    setGridLineDragMode(null);
  }, [
    setIsDraggingGridLine,
    setDraggingGridLine,
    setGridLineStartPos,
    setIsPlacingGridLine,
    setPlacingGridLineType,
    setGridLineDragMode,
  ]);

  return {
    // Grid line movement operations
    onMouseDown,
    onMouseMove,
    onMouseUp,
    startGridLineDrag,
    continueGridLineDrag,
    finishGridLineDrag,

    // Row/column addition operations
    addRowAbove,
    addRowBelow,
    addColumnLeft,
    addColumnRight,
    performColumnSplit,

    // Grid line placement operations
    startPlacingGridLine,
    confirmGridLinePlacement,
    cancelGridLinePlacement,

    // Utility operations
    resetGridLineState,

    // Information
    getGridLineStats,
    isInGridLineMode,

    // Direct access to collections for advanced use
    dynamicGridLines,
    originalGridLines,
    isDraggingGridLine,
    isPlacingGridLine,
    addRowColumnFunctions, // Expose entire object for utilities that need it
  };
};

export default useGridLineOperations;
